import { EventType } from "@/analytics/analytics-events";
import { EditElementProperties } from "@/analytics/data-organization-events-properties";
import { useWaypointReference } from "@/components/r3f/utils/placeholder-preview";
import { isIElementWaypoint } from "@/hooks/use-placeholders";
import { EditElementNameInput } from "@/modes/sheet-mode/edit-waypoints/edit-waypoint-context-menu/edit-element-name-input";
import { useAppSelector } from "@/store/store-hooks";
import { Analytics } from "@faro-lotv/foreign-observers";
import { assert } from "@faro-lotv/foundation";
import { selectIElement } from "@faro-lotv/project-source";
import { Box } from "@mui/material";
import { useCallback, type ComponentProps, type JSX } from "react";
import { ContextMenuActionType, type ContextMenuAction } from "../action-types";

function EditWaypointNameAction({
  elementId,
  closeContextMenu,
  dark,
}: ComponentProps<
  NonNullable<ContextMenuAction["CustomComponent"]>
>): JSX.Element | null {
  const element = useAppSelector(selectIElement(elementId));
  const waypoint = element && isIElementWaypoint(element) ? element : undefined;

  const reference = useWaypointReference(waypoint);

  const onNameEdited = useCallback(() => {
    assert(
      reference,
      "Reference must be defined to track a capture-tree-context-menu action",
    );
    Analytics.track<EditElementProperties>(EventType.editElement, {
      via: "capture tree context menu",
      elementType: reference.type,
      elementTypeHint: reference.typeHint ?? undefined,
      hasEditedName: true,
      hasEditedTimePoint: false,
      hasEditedTags: false,
      hasEditedColor: false,
    });

    closeContextMenu();
  }, [closeContextMenu, reference]);

  if (!reference) return null;

  return (
    <Box aria-label="edit waypoint name" component="div" sx={{ px: 1 }}>
      <EditElementNameInput
        dark={dark}
        element={reference}
        onNameChanged={onNameEdited}
      />
    </Box>
  );
}

/**
 * Action to edit the name of a waypoint.
 * It uses a custom component with a text field to render the name and make it editable.
 */
export const EDIT_WAYPOINT_NAME_ACTION: ContextMenuAction = {
  type: ContextMenuActionType.editWaypointName,
  CustomComponent: EditWaypointNameAction,
};
